import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useGlobals } from 'hooks/useGlobals';
import { junip } from 'lib/junip';
import type { JunipProduct } from 'types/reviews';
import type { JunipProductRaw, JunipProductsRaw } from 'types/reviews/rawData';
import { convertArrayToCamelCase } from 'utils/convertKeysToCamelCase';
import { reviewedProductsSelector } from 'utils/reviews/reviewedProductsSelector';

export const REVIEWED_PRODUCTS_KEY = 'reviewedProducts';

export type UseReviewedProductsQueryKey = [string];

export type UseReviewedProductsSelector = JunipProduct[] | undefined;

export interface UseReviewedProductsOptions {
  slugsToFilter?: string[];
}

export const fetchReviewedProducts = async (): Promise<JunipProduct[]> => {
  const res = await junip<JunipProductsRaw>(
    '/v1/products?page[size]=100&include=active_traits&filter[unreviewable]=false',
  );
  return convertArrayToCamelCase<JunipProductRaw, JunipProduct>(res.products);
};

export const useReviewedProducts = ({
  slugsToFilter,
}: UseReviewedProductsOptions): UseQueryResult<
  UseReviewedProductsSelector,
  unknown
> => {
  const { data: globals } = useGlobals();

  return useQuery<
    JunipProduct[],
    unknown,
    UseReviewedProductsSelector,
    UseReviewedProductsQueryKey
  >({
    queryKey: [REVIEWED_PRODUCTS_KEY],
    queryFn: fetchReviewedProducts,
    select: (data): UseReviewedProductsSelector =>
      reviewedProductsSelector({
        globals,
        reviewedProducts: data,
        slugsToFilter,
      }),
  });
};
